import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import AppShell from "./layouts/AppShell";
import Login from "./pages/Login";
import GoogleCallback from "./pages/GoogleCallback";
import Dashboard from "./pages/Dashboard";
import Forbidden from "./pages/Forbidden";
import PermGuard from "./components/PermGuard";
import UsersAdmin from "./pages/admin/Users";
import RolesAdmin from "./pages/admin/Roles";
import AuditLog from "./pages/admin/AuditLog";
import FamiliesPage from "./modules/families/FamiliesPage";
import StudentsPage from "./modules/students/StudentsPage";
import LeadsPage from "./modules/leads/LeadsPage";
import ClassesPage from "./modules/classes/ClassesPage";
import CalendarPage from "./modules/calendar/CalendarPage";
import HolidaysPage from "./modules/holidays/HolidaysPage";
import AttendancePage from "./modules/attendance/AttendancePage";
import GradesPage from "./modules/grades/GradesPage";
import InvoicesPage from "./modules/invoices/InvoicesPage";
import DefaultersPage from "./modules/defaulters/DefaultersPage";
import SepaPage from "./modules/sepa/SepaPage";
import PayrollPage from "./modules/payroll/PayrollPage";
import ResourcesPage from "./modules/resources/ResourcesPage";
import SignPage from "./modules/signatures/SignPage";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminGDPRPage from "./pages/admin/GDPRPage";
import AdminRoomsPage from "./pages/admin/RoomsPage";
import Profile from "./pages/Profile";
import { FEATURES } from "./config/features";
import "./App.css";
import "./i18n";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/403" element={<Forbidden />} />

          <Route path="/sign/:id" element={<SignPage />} />

          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />

            <Route path="/families" element={<PermGuard module="families"><FamiliesPage /></PermGuard>} />
            <Route path="/students" element={<PermGuard module="students"><StudentsPage /></PermGuard>} />
            {FEATURES.leads ? (
              <>
                <Route path="/leads" element={<PermGuard module="leads"><LeadsPage /></PermGuard>} />
                <Route path="/leads/*" element={<PermGuard module="leads"><LeadsPage /></PermGuard>} />
              </>
            ) : (
              <>
                <Route path="/leads" element={<Navigate to="/dashboard" replace />} />
                <Route path="/leads/*" element={<Navigate to="/dashboard" replace />} />
              </>
            )}
            <Route path="/classes" element={<PermGuard module="classes"><ClassesPage /></PermGuard>} />
            <Route path="/calendar" element={<PermGuard module="calendar"><CalendarPage /></PermGuard>} />
            <Route path="/attendance" element={<PermGuard module="attendance"><AttendancePage /></PermGuard>} />
            <Route path="/grades" element={<PermGuard module="grades"><GradesPage /></PermGuard>} />
            <Route path="/invoices" element={<PermGuard module="invoices"><InvoicesPage /></PermGuard>} />
            <Route path="/defaulters" element={FEATURES.defaulters ? (
              <PermGuard module="defaulters"><DefaultersPage /></PermGuard>
            ) : (
              <Navigate to="/dashboard" replace />
            )} />
            <Route path="/defaulters/*" element={FEATURES.defaulters ? (
              <PermGuard module="defaulters"><DefaultersPage /></PermGuard>
            ) : (
              <Navigate to="/dashboard" replace />
            )} />
            <Route path="/sepa" element={<PermGuard module="sepa"><SepaPage /></PermGuard>} />
            <Route path="/payroll" element={FEATURES.payroll ? (
              <PermGuard module="payroll"><PayrollPage /></PermGuard>
            ) : (
              <Navigate to="/dashboard" replace />
            )} />
            <Route path="/payroll/*" element={FEATURES.payroll ? (
              <PermGuard module="payroll"><PayrollPage /></PermGuard>
            ) : (
              <Navigate to="/dashboard" replace />
            )} />
            <Route path="/messages" element={<Navigate to="/dashboard" replace />} />
            <Route path="/messages/*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/resources" element={FEATURES.resources ? (
              <PermGuard module="resources"><ResourcesPage /></PermGuard>
            ) : (
              <Navigate to="/dashboard" replace />
            )} />
            <Route path="/resources/*" element={FEATURES.resources ? (
              <PermGuard module="resources"><ResourcesPage /></PermGuard>
            ) : (
              <Navigate to="/dashboard" replace />
            )} />
            <Route path="/profile" element={<Profile />} />

            <Route path="/admin/users" element={<PermGuard module="users"><UsersAdmin /></PermGuard>} />
            <Route path="/admin/roles" element={<PermGuard module="roles"><RolesAdmin /></PermGuard>} />
            <Route path="/admin/audit" element={<PermGuard module="audit_log"><AuditLog /></PermGuard>} />
            <Route path="/admin/holidays" element={<PermGuard module="calendar"><HolidaysPage /></PermGuard>} />
            <Route path="/admin/settings" element={<PermGuard module="users"><AdminSettings /></PermGuard>} />
            <Route path="/admin/gdpr" element={<PermGuard module="users"><AdminGDPRPage /></PermGuard>} />
            <Route path="/admin/rooms" element={<PermGuard module="rooms"><AdminRoomsPage /></PermGuard>} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
